import { track } from '@/flow/instrument';
import { useT } from '@/i18n/runtime';
import { useCaptureStore } from '@/store/captureStore';
import { useEffect } from 'react';
import { Icon } from './Icon';

/**
 * "Saved, will send" (SPEC_CAPTURE_UX_R2 §8 #1): the existing store-and-forward queue made visible — the representative
 * sees that a stalled upload is SAFE. Reads the store's queue facts (pending chunks, online, persistence) and says the
 * one honest line for them; nothing when nothing is pending and the phone is online. Never a blocker.
 */
export function SavedWillSend(): JSX.Element | null {
  const { t } = useT();
  const pending = useCaptureStore((s) => s.pendingChunks);
  const online = useCaptureStore((s) => s.online);
  const persistence = useCaptureStore((s) => s.persistence);
  const shown = pending > 0 || !online;
  const state = !online ? 'offline' : 'pending';
  // §10 "Offline indicator shown → % of sessions touching it, by region": emitted when the queue ENTERS
  // store-and-forward, not on every re-render, so the metric counts sessions and not paint cycles.
  useEffect(() => {
    if (shown) track('offline.shown', { state });
  }, [shown, state]);
  if (!shown) return null;
  // <output> carries the implicit "status" role — the semantic element, not a div with a role.
  return (
    <output className={`sv-saved is-${state}`} data-testid="saved-will-send" data-state={state} data-pending={pending} aria-live="polite">
      <Icon name="cloud" size={14} />
      <span>
        {online ? t('sv.queue.saved') : t('sv.queue.savedOffline')}
        {pending === 1 ? ` · ${t('sv.queue.onePiece')}` : pending > 1 ? ` · ${t('sv.capture.uploading', { n: String(pending) })}` : ''}
      </span>
      {persistence === 'memory' ? <span className="sv-saved-note">{t('sv.queue.keepOpen')}</span> : null}
    </output>
  );
}
